import React from 'react';
import classes from './DocumentTable.module.scss'
import { Button, Dropdown, MenuProps, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { MenuItemType } from 'antd/es/menu/interface';
import { useAppDispatch, useAppSelector } from 'store/hook';
import downloadCascadePermissionCases from 'components/PermissionsByFolder/cascadePermissionsRules/canDownload';
import { convertableTypesFiles } from 'helpers/filesExtensions';
import { handleDownload, requestDeleteEntity } from 'store/reducers/documentsCreator';
import { setIsOpenMoveEntityModal } from 'store/slices/windowStateSlice';
import { renderSelectedEntities } from 'helpers/renderSelectedEntities';
import Assets from 'assets/icons';
import cn from 'classnames';
import { formatBytes, getDeclension } from 'helpers/selectedObjects';
import { choosenDocument } from 'store/slices/dataDocumentsSlice';
import { getTheme } from 'helpers/getTheme';

interface DocumentsFooterProps {
  handleDownloadArchive?: () => void;
}

const DocumentsFooter = ({handleDownloadArchive}: DocumentsFooterProps) => {
  
  const { choosenDocuments, isLoadingDocuments, documents, nestedDocuments, folders, nestedFolders, filesForDownloading, currentFolder } = useAppSelector((store) => store.documents);
  const { isDeletingDocuments, messageApi } = useAppSelector((store) => store.windowState);
  const { userFoldersPermissions, userRootPermissions } = useAppSelector((store) => store.permissions);
  const allDocuments = React.useMemo(() => [...documents, ...nestedDocuments], [documents, nestedDocuments]);
  const allFolders = React.useMemo(() => [...folders, ...nestedFolders], [folders, nestedFolders]);
  const { t, i18n } = useTranslation();
  const tokensForDownloading = filesForDownloading.map(file => file?.token || '');
  
  const { choosenFiles, choosenFolders, selectedDocuments } = React.useMemo(() => {
    const choosenFiles = choosenDocuments.filter(({ key }) => !(key as string).startsWith('folder'));
    const choosenFolders = choosenDocuments.filter(({ key }) => (key as string).startsWith('folder'));
    const selectedDocuments = choosenDocuments.map(({ permissions }) => permissions);

    return { choosenFiles, choosenFolders, selectedDocuments };
  }, [choosenDocuments]);

  const dispatch = useAppDispatch();

  const isRootFolder = !currentFolder[0];
  const targetFolderPermissions = (isRootFolder && userRootPermissions)
    ? userRootPermissions.permissions
    : userFoldersPermissions?.find((folder) => folder.id === currentFolder[0]?.id)?.permissions;
  const isAvailableFoldersToMove = Boolean(currentFolder[0] || folders[0]);

  const countFiles = getDeclension(choosenFiles.length, [t('Documents.file1'), t('Documents.file2'), t('Documents.file3')])
  const countFolders = getDeclension(choosenFolders.length, [t('Documents.folder1'), t('Documents.folder2'), t('Documents.folder3')]);
  const countOfEntities = `${t('Documents.selected')} ${countFiles}, ${countFolders}`;  

  const filesSize = choosenFiles.reduce((acc: number, file: choosenDocument) => {
    if (file.size) {
      return acc += file.size
    } else {
      return acc;
    }
  }, 0);

  const handlePdfDownload = () => dispatch(handleDownload({is_webview: true, language: i18n.language, t, messageApi: messageApi!}));
  const handleDocumentDownload = () => dispatch(handleDownload({is_webview: false, language: i18n.language, t, messageApi: messageApi!}));
  const handleArchiveDownload = () => {
    if (handleDownloadArchive) {
      handleDownloadArchive();
    }
  };
  const onMoveClick = () => dispatch(setIsOpenMoveEntityModal(true));

  const onDeleteClick = async () => {
    let entity: any;
    if (choosenFolders[0]) {
      entity = {...choosenFolders[0], type: 'folder'}
    } else if (choosenDocuments[0]) {
      entity = {...choosenDocuments[0], type: 'file'}
    }

    entity && await dispatch(requestDeleteEntity({entity}));
  }

  const { isSomeChoosenDocumentsForbiddenToDelete, downloadDocumentsStatus, isAvailableDownloadArchive, isAvailableMoveDocuments } = React.useMemo(() => {
    let downloadDocumentsStatus: 'download' | 'downloadAsPdf' | 'downloadAsDoc' | 'disabled' = 'disabled';
    const canDownloadCases = selectedDocuments.map((documentPermissions) =>
      downloadCascadePermissionCases
        .map((perms) => perms.every((el) => documentPermissions?.includes(el)))
        .includes(true)
    );

    const isCanDownload = !canDownloadCases.includes(false);
    const isSomeChoosenDocumentsForbiddenToDownload = selectedDocuments
      .map((documentPermissions) => documentPermissions?.includes('can_download'))
      .includes(false);
    const isSomeChoosenDocumentsForbiddenToDelete = selectedDocuments
      .map((documentPermissions) => documentPermissions?.includes('can_delete'))
      .includes(false);
    const isSomeChoosenDocumentsForbiddenToDownloadPDF = selectedDocuments
      .map((documentPermissions) => documentPermissions?.includes('can_download_pdf'))
      .includes(false);

    if (
      !isSomeChoosenDocumentsForbiddenToDownloadPDF &&
      (!isSomeChoosenDocumentsForbiddenToDownload || isCanDownload)
    ) {
      downloadDocumentsStatus = 'download';
    } else if (!isSomeChoosenDocumentsForbiddenToDownloadPDF || !isSomeChoosenDocumentsForbiddenToDownload) {
      downloadDocumentsStatus = !isSomeChoosenDocumentsForbiddenToDownloadPDF ? 'downloadAsPdf' : 'downloadAsDoc';
    } else if (isCanDownload) {
      downloadDocumentsStatus = 'downloadAsDoc';
    } else {
      downloadDocumentsStatus = 'disabled';
    }

    if (!isSomeChoosenDocumentsForbiddenToDownload || isCanDownload) {
      for (let current in choosenDocuments) {
        const extension = choosenDocuments[current]?.extension?.toLocaleLowerCase() || '';
        const unavailableExtansion = !convertableTypesFiles.includes(extension);
        
        const id = (choosenDocuments[current]?.key as string).split(' ')[2];
        const signedEl = allDocuments.find((doc) => doc.id === id);
        const haveSomeFileWithElectronicSignature = signedEl ? signedEl.signing_status !== null : false;      
  
        if (unavailableExtansion || haveSomeFileWithElectronicSignature) {
          downloadDocumentsStatus = 'downloadAsDoc';
          break;
        }
      };
    };
    
    const isAvailableDownloadArchive =
      selectedDocuments.every((documentPermissions) => documentPermissions?.includes('can_download')) &&
      choosenDocuments.length > 0;

    const isAvailableMoveDocuments =
      targetFolderPermissions?.includes('can_delete') && isAvailableFoldersToMove;

    return {
      isSomeChoosenDocumentsForbiddenToDownload,
      isSomeChoosenDocumentsForbiddenToDelete,
      isSomeChoosenDocumentsForbiddenToDownloadPDF,
      downloadDocumentsStatus,
      isAvailableDownloadArchive,
      isAvailableMoveDocuments,
    };
  }, [selectedDocuments, targetFolderPermissions, isAvailableFoldersToMove]);

  const isDisabledDeleteButton =
    (choosenFiles.length === 0 && choosenFolders.length === 0) ||
    isSomeChoosenDocumentsForbiddenToDelete ||
    isDeletingDocuments;

  const { namesOfChoosenFiles, namesOfChoosenFolders } = React.useMemo(
    () => renderSelectedEntities(choosenDocuments, allDocuments, allFolders, t),
    [choosenDocuments, allDocuments, allFolders, t]
  );

  const deleteText = () => (<div className={classes.descriptionTextWrap}>
    <span>{t('Documents.confirm.confirmDelete')}:</span>
    {namesOfChoosenFolders.map((folder) => folder)}
    {namesOfChoosenFiles.map((file) => (file))}
  </div>)

  const downloadItems: MenuProps['items'] = [
    {
      label: (
        <Button
          onClick={handleDocumentDownload}
          icon={<Assets.DownloadIcon />}
          type='default'
          loading={Boolean(tokensForDownloading[0])}
          disabled={choosenFiles.length === 0 || isLoadingDocuments || Boolean(tokensForDownloading[0])}
          className={cn(classes.dropdwnBtn, classes.downloadBtn)}
        >
          {t('Documents.management.downloadAsDoc')}
        </Button>
      ),
      key: 0,
      className: classes.liItem,
    },

    {
      label: (
        <Button
          onClick={handlePdfDownload}
          icon={<Assets.FilePdfIcon />}
          type='default'
          loading={Boolean(tokensForDownloading[0])}
          disabled={choosenFiles.length === 0 || isLoadingDocuments || Boolean(tokensForDownloading[0])}
          className={cn(classes.dropdwnBtn, classes.downloadBtn)}
        >
          {t('Documents.management.downloadAsPdf')}
        </Button>
      ),
      key: 1,
      className: classes.liItem,
    },
  ];

  const downloadButtonCases = {
    download: (
      <Dropdown
        menu={{ items: downloadItems, className: getTheme() }}
        // align={{ offset: [0, 0] }}
        disabled={choosenFiles.length === 0 || isLoadingDocuments || Boolean(tokensForDownloading[0])}
        trigger={['click']}
        placement='top'
      >
        <Button
          icon={<Assets.DownloadIcon />}
          type='default'
          loading={Boolean(tokensForDownloading[0])}
          className={cn(classes.ftrBtn, classes.downloadBtn)}
        >
          {t('Documents.management.download')}
        </Button>
      </Dropdown>
    ),
    downloadAsDoc: (downloadItems[0] as MenuItemType).label,
    downloadAsPdf: (downloadItems[1] as MenuItemType).label,
    disabled: (
      <Button
        icon={<Assets.DownloadIcon />}
        type='default'
        loading={Boolean(tokensForDownloading[0])}
        disabled
        className={cn(classes.ftrBtn, classes.downloadBtn)}
      >
        {t('Documents.management.download')}
      </Button>
    ),
  };

  return (
    <div className={classes.tableFooter}>
      <div className={classes.selected}>
        <div>{countOfEntities}</div>
        {/* <div className={classes.size}>{t('Documents.totalSize')}{formatBytes(filesSize, i18n.language)}</div> */}
      </div>
      <div className={classes.buttonsGroup}>
        <Tooltip
          mouseEnterDelay={0.5}
          title={!isAvailableMoveDocuments ? t('Documents.management.needPermission') : ''}
        >
          <Button
            type='default'
            icon={<Assets.RelocateIcon/>}
            disabled={!isAvailableMoveDocuments || isLoadingDocuments}
            onClick={onMoveClick}
            className={cn(classes.moveBtn, classes.ftrBtn)}
          >
            {t('Documents.contextMenu.move')}
          </Button>
        </Tooltip>
        <Button
          variant='solid'
          color='danger'
          disabled={isDisabledDeleteButton}
          loading={isDeletingDocuments}
          onClick={onDeleteClick}
          className={classes.ftrBtn}
          icon={
            <Assets.TrashIcon
              className={cn(classes.iconTrash,
                (choosenFiles.length === 0 && choosenFolders.length === 0) ||
                isSomeChoosenDocumentsForbiddenToDelete
                  ? classes.grey
                  : classes.red
              )}
            />
          }
        >
          {t('Documents.management.delete')}
        </Button>

        {!choosenFolders.length && downloadButtonCases[downloadDocumentsStatus]}

        <Tooltip
          mouseEnterDelay={.5}
          title={isAvailableDownloadArchive
            ? t('Documents.management.downloadAsZip')
            : t('Documents.management.needPermission')
          }
        >
          <Button
            icon={<Assets.FileZipIcon />}
            type='default'
            onClick={handleArchiveDownload}
            loading={Boolean(tokensForDownloading[0])}
            disabled={tokensForDownloading.length !== 0 || !isAvailableDownloadArchive || isLoadingDocuments}
            className={cn(classes.ftrBtn, classes.downloadBtn)}
          >
            {t('Documents.management.downloadAsArchive')}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

export default DocumentsFooter;
